import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PageLayout from '@/common-components/page-layout/page-layout';
import { useFindSessionsByNodeEventQuery } from '@common/reducers/backend-api/backend-api';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EventType } from '@common/models/node-event';
import { Button } from '@/components/ui/button';

const DEFAULT_LIMIT = 20;

export const SessionListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Extract filter parameters from URL
  const eventType = searchParams.get('eventType') as EventType | null;
  const processType = searchParams.get('processType') || null;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || `${DEFAULT_LIMIT}`, 10);
  const skip = (currentPage - 1) * limit;

  const queryParams = useMemo(() => ({
    ...(eventType && { eventType }),
    ...(processType && { processType }),
    limit,
    skip,
  }), [eventType, processType, limit, skip]);

  // Fetch sessions based on filters
  const { data: sessionsData, isLoading, isError, error } = useFindSessionsByNodeEventQuery(queryParams, {
    skip: !eventType && !processType, // Skip query if no filters are present
  });

  // Calculate pagination details
  const totalItems = sessionsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / limit);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Handle page navigation
  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    navigate(`?${newSearchParams.toString()}`);
  };

  const pageTitle = useMemo(() => {
    if (eventType) return `Sessions with Event Type: ${eventType}`;
    if (processType) return `Sessions with Process Type: ${processType}`;
    return 'Session List';
  }, [eventType, processType]);

  const pageDescription = useMemo(() => {
    if (eventType) return `Displaying sessions that recorded a node event of type ${eventType}`;
    if (processType) return `Displaying sessions that recorded a PROCESS node event with process type ${processType}`;
    return 'A list of automation sessions.';
  }, [eventType, processType]);

  return (
    <PageLayout title={pageTitle}>
      <p className="text-slate-500 mb-4">{pageDescription}</p>
      
      {isLoading && (
        <div className="h-60 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>Error Loading Sessions</AlertTitle>
          <AlertDescription>
            {(error as any)?.data?.message || (error as any)?.error || 'An unknown error occurred'}
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && sessionsData && (
        <> 
          {sessionsData.data.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-slate-500">
              No sessions found matching the criteria.
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Session ID</TableHead>
                    <TableHead>Bot ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionsData.data.map((session) => (
                    <TableRow 
                      key={session._id}
                      onClick={() => navigate(`/bot-detail-view/${session.botId}/session/${session._id}`)}
                      className="cursor-pointer hover:bg-muted/50"
                    >
                      <TableCell className="font-mono text-xs">{session._id}</TableCell>
                      <TableCell className="font-mono text-xs">{session.botId}</TableCell>
                      <TableCell>{session.status}</TableCell>
                      <TableCell>{new Date(session.createdAt).toLocaleString()}</TableCell>
                      <TableCell className="text-right">{new Date(session.updatedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-end space-x-2 py-4">
                  <div className="flex-1 text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages} ({totalItems} total sessions)
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!canGoPrevious}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!canGoNext}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!isLoading && !isError && !sessionsData && !eventType && !processType && (
         <Alert variant="destructive">
          <AlertTitle>Missing Filter</AlertTitle>
          <AlertDescription>
            Please specify an eventType or processType query parameter.
          </AlertDescription>
        </Alert>
      )}

    </PageLayout>
  );
}; 